<template>
  <v-alert
    v-if="showAlert"
    closable
    class="font-weight-medium"
    rounded="0"
    data-testid="v-alert-test"
    density="compact"
    :text="latestMessage"
    :type="isUrgent ? 'error' : 'warning'"
  >
    <template #close>
      <v-btn
        icon="mdi-close"
        data-testid="alert-close-button"
        color="black"
        variant="text"
        @click="dismissBanner()"
      />
    </template>
  </v-alert>
  <v-alert
    v-if="showRefreshAlert"
    data-testid="v-alert-refresh"
    density="compact"
    class="font-weight-medium"
    rounded="0"
    type="error"
  >
    Refreshing the page in {{ countdown }} seconds...
  </v-alert>
</template>

<script lang="ts">
import {mapState} from 'vuex';
import {currentUser, getDocument} from '@/services/FirestoreService';
import {Nullable} from '@/types/commonTypes';

type IntervalHandle = Nullable<ReturnType<typeof setInterval>>;
type TimeoutHandle = Nullable<ReturnType<typeof setTimeout>>;

interface BannerSegment {
  currentMessage?: string;
  isUrgent?: boolean;
  forceRefresh?: Date | null;
}

interface BannerConfig extends BannerSegment {
  anonymous?: BannerSegment;
}

export const REFRESH_TIMEOUT = (1000 * 60); // 1 minute
export const CONFIG_CHECK_INTERVAL = (1000 * 60 * 2); // 2 minutes

export default {
  name: 'MapBanner',
  data() {
    return {
      showAlert: false,
      showRefreshAlert: false,
      intervalId: null as IntervalHandle,
      countdownIntervalId: null as IntervalHandle,
      refreshTimeoutId: null as TimeoutHandle,
      latestMessage: '',
      isUrgent: false,
      countdown: REFRESH_TIMEOUT / 1000,
      mountedAt: new Date(),
    };
  },
  computed: {
    ...mapState(['configCollectionName']),
  },
  mounted() {
    // Using .then() allows this component to complete mounting and display the banner whenever it is available without
    // blocking the UI.
    this.refreshBanner(true).then(() => {
      this.intervalId = setInterval(() => this.refreshBanner(), CONFIG_CHECK_INTERVAL);
    });
  },
  beforeUnmount() {
    this.clearPollingInterval();
    this.clearForceRefreshTimers();
  },
  methods: {
    /** Dismisses the provided message and saves it to storage. */
    dismissBanner() {
      window.localStorage.setItem('last-dismissed-message', this.latestMessage);
      this.showAlert = false;
    },
    async refreshBanner(isInitial = false) {
      const banner = await this.fetchBannerConfig();
      if (!banner) return null;

      const authenticated = Boolean(currentUser()?.uid);
      const segment = authenticated ? banner ?? undefined : banner.anonymous;
      if (!segment) return null;

      this.applyBannerMessage(segment);

      // If this is the initial load, the value of forceRefresh is irrelevant, there is no need to reload the page.
      if (isInitial) return;
      this.evaluateForceRefresh(segment.forceRefresh);
    },
    async fetchBannerConfig(): Promise<Nullable<BannerConfig>> {
      try {
        return await getDocument(this.configCollectionName, 'banner') as Nullable<BannerConfig>;
      } catch (error) {
        console.error('Error retrieving banner configuration', error);
        return null;
      }
    },
    applyBannerMessage(banner: Nullable<BannerConfig>) {
      this.latestMessage = banner?.currentMessage ?? '';
      this.isUrgent = Boolean(banner?.isUrgent);

      // If the message has been cleared on the server, clear it locally.
      if (!this.latestMessage) {
        this.showAlert = false;
        window.localStorage.removeItem('last-dismissed-message');
        return;
      }

      // If a message has been dismissed previously, only show it if it's new.
      const lastDismissedMessage = window.localStorage.getItem('last-dismissed-message');
      if (lastDismissedMessage !== this.latestMessage) {
        this.showAlert = true;
        window.localStorage.removeItem('last-dismissed-message');
        return;
      }

      this.showAlert = false;
    },
    evaluateForceRefresh(timestamp: unknown) {
      const forceRefreshTimestamp = this.normalizeForceRefresh(timestamp);
      if (!forceRefreshTimestamp) return;

      // If the force refresh timestamp is more recent than when the component was mounted, start the countdown.
      // Use getTime() to compare the Unix epoch in UTC to prevent issues with time zones.
      if (forceRefreshTimestamp.getTime() > this.mountedAt.getTime()) {
        this.startForceRefreshCountdown();
      }
    },
    startForceRefreshCountdown() {
      this.clearForceRefreshTimers();
      this.showRefreshAlert = true;
      this.countdown = REFRESH_TIMEOUT / 1000;
      this.startCountdown();
      this.refreshTimeoutId = setTimeout(() => {
        window.location.reload();
      }, REFRESH_TIMEOUT);
    },
    startCountdown() {
      this.clearCountdownInterval();
      this.countdownIntervalId = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown -= 1;
          return;
        }
        this.clearCountdownInterval();
      }, 1000);
    },
    clearForceRefreshTimers() {
      this.clearCountdownInterval();
      this.clearRefreshTimeout();
    },
    clearCountdownInterval() {
      if (this.countdownIntervalId) {
        clearInterval(this.countdownIntervalId);
        this.countdownIntervalId = null;
      }
    },
    clearRefreshTimeout() {
      if (this.refreshTimeoutId) {
        clearTimeout(this.refreshTimeoutId);
        this.refreshTimeoutId = null;
      }
    },
    clearPollingInterval() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },
    normalizeForceRefresh(value: unknown): Nullable<Date> {
      if (!(value instanceof Date)) {
        return null;
      }

      return Number.isNaN(value.getTime()) ? null : value;
    },
  },
};
</script>
